import { create } from "zustand";
import { API } from "@/lib/apiConfig";

/**
 * Settlement Ledger v3 - Sovereign Reconciliation Store
 * 
 * Manages country-level reconciliation of CO₂ and ESG credits between countries,
 * generates compliance audit trails, and exports EU-ready JSON with eSeal signatures.
 */

export type EUCountry = 
  | "AT" | "BE" | "BG" | "CY" | "CZ" | "DE" | "DK" | "EE" | "ES" | "FI"
  | "FR" | "GR" | "HR" | "HU" | "IE" | "IT" | "LT" | "LU" | "LV" | "MT"
  | "NL" | "PL" | "PT" | "RO" | "SE" | "SI" | "SK";

export interface ReconciliationEntry {
  id: string;
  ts: number;
  fromCountry: EUCountry;
  toCountry: EUCountry;
  amount: number; // CO₂/ESG credits
  scope: "SCOPE1" | "SCOPE2" | "SCOPE3" | "EMBODIED" | "ETS" | "SUBSIDY";
  reason: string;
  complianceAuditTrail: string[]; // Audit trail entries
  eSealHash?: string; // eSeal signature hash
  exported: boolean;
  meta?: any;
}

export interface ComplianceAuditTrail {
  id: string;
  ts: number;
  country: EUCountry;
  action: "reconciliation" | "export" | "verification" | "dispute";
  details: string;
  entryId?: string;
  verified: boolean;
}

export interface SovereignReconciliationStoreState {
  // Reconciliation entries
  reconciliations: ReconciliationEntry[];
  
  // Compliance audit trail
  auditTrail: ComplianceAuditTrail[];
  
  // Country balances (aggregated from Settlement Ledger)
  countryBalances: Record<EUCountry, number>;
  
  // Actions
  createReconciliation: (
    fromCountry: EUCountry,
    toCountry: EUCountry,
    amount: number,
    scope: ReconciliationEntry["scope"],
    reason: string,
    onSettlementLedgerTransfer?: (from: string, to: string, amount: number, reason: string, scope: string) => void
  ) => Promise<void>;
  
  verifyReconciliation: (entryId: string) => void;
  disputeReconciliation: (entryId: string, reason: string) => void;
  
  // Export with eSeal
  exportReconciliationReport: (country?: EUCountry) => Promise<string>; // Returns eSeal hash
  
  // Aggregation
  getCountryBalance: (country: EUCountry) => number;
  getReconciliationSummary: () => {
    totalReconciliations: number;
    totalAmount: number;
    byCountry: Record<EUCountry, { sent: number; received: number; net: number }>;
  };
  
  // Audit
  addAuditEntry: (country: EUCountry, action: ComplianceAuditTrail["action"], details: string, entryId?: string) => void;
  getAuditTrail: (country?: EUCountry) => ComplianceAuditTrail[];
}

const API_BASE = API.sovereignReconciliation;

function generateId(): string {
  return `recon_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
}

export const useSovereignReconciliationStore = create<SovereignReconciliationStoreState>((set, get) => ({
  reconciliations: [],
  auditTrail: [],
  countryBalances: {} as Record<EUCountry, number>,

  createReconciliation: async (fromCountry, toCountry, amount, scope, reason, onSettlementLedgerTransfer) => {
    // Try to sync with backend if available
    let entry: ReconciliationEntry;
    try {
      const response = await fetch(`${API_BASE}/reconciliations`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ fromCountry, toCountry, amount, scope, reason }),
      });
      if (response.ok) {
        const data = await response.json();
        entry = data.entry;
      } else {
        throw new Error("Backend request failed");
      }
    } catch (error) {
      console.log("Backend not available, using local state only");
      // Fallback to local creation
      entry = {
        id: generateId(),
        ts: Date.now(),
        fromCountry,
        toCountry,
        amount,
        scope,
        reason,
        complianceAuditTrail: [
          `Reconciliation created: ${amount} ${scope} credits from ${fromCountry} to ${toCountry}`,
          `Reason: ${reason}`,
          `Timestamp: ${new Date().toISOString()}`,
        ],
        exported: false,
      };
    }
    
    // Record in Settlement Ledger (via callback from component)
    if (onSettlementLedgerTransfer) {
      onSettlementLedgerTransfer(
        fromCountry,
        toCountry,
        amount,
        `SOVEREIGN_RECONCILIATION:${reason}`,
        scope
      );
    }
    
    // Update country balances
    set((state) => {
      const balances = { ...state.countryBalances };
      balances[fromCountry] = (balances[fromCountry] || 0) - amount;
      balances[toCountry] = (balances[toCountry] || 0) + amount;
      
      return {
        reconciliations: [...state.reconciliations, entry],
        countryBalances: balances,
      };
    });
    
    // Add audit entry
    get().addAuditEntry(fromCountry, "reconciliation", `Reconciled ${amount} ${scope} to ${toCountry}`, entry.id);
  },

  verifyReconciliation: (entryId) => {
    set((state) => {
      const reconciliations = state.reconciliations.map((r) =>
        r.id === entryId
          ? {
              ...r,
              complianceAuditTrail: [
                ...r.complianceAuditTrail,
                `Verified: ${new Date().toISOString()}`,
              ],
            }
          : r
      );
      
      const entry = reconciliations.find((r) => r.id === entryId);
      if (entry) {
        get().addAuditEntry(entry.fromCountry, "verification", `Reconciliation ${entryId} verified`, entryId);
      }
      
      return { reconciliations };
    });
  },

  disputeReconciliation: (entryId, reason) => {
    set((state) => {
      const reconciliations = state.reconciliations.map((r) =>
        r.id === entryId
          ? {
              ...r,
              complianceAuditTrail: [
                ...r.complianceAuditTrail,
                `Disputed: ${reason} - ${new Date().toISOString()}`,
              ],
            }
          : r
      );
      
      const entry = reconciliations.find((r) => r.id === entryId);
      if (entry) {
        get().addAuditEntry(entry.fromCountry, "dispute", `Reconciliation ${entryId} disputed: ${reason}`, entryId);
      }
      
      return { reconciliations };
    });
  },

  exportReconciliationReport: async (country) => {
    const state = get();
    const reconciliations = country
      ? state.reconciliations.filter((r) => r.fromCountry === country || r.toCountry === country)
      : state.reconciliations;
    
    const report = {
      version: "3.0",
      exportedAt: new Date().toISOString(),
      country: country || "ALL",
      reconciliations: reconciliations.map((r) => ({
        id: r.id,
        ts: r.ts,
        fromCountry: r.fromCountry,
        toCountry: r.toCountry,
        amount: r.amount,
        scope: r.scope,
        reason: r.reason,
        complianceAuditTrail: r.complianceAuditTrail,
        eSealHash: r.eSealHash,
      })),
      countryBalances: state.countryBalances,
      auditTrail: state.getAuditTrail(country),
      summary: state.getReconciliationSummary(),
    };
    
    // Generate eSeal hash (simplified - in production would use actual eSeal service)
    const reportString = JSON.stringify(report, null, 2);
    const eSealHash = `0x${Array.from(reportString)
      .map((c) => c.charCodeAt(0).toString(16).padStart(2, "0"))
      .join("")
      .substring(0, 64)}`;
    
    // Update entries with eSeal hash
    set((state) => ({
      reconciliations: state.reconciliations.map((r) =>
        reconciliations.some((rec) => rec.id === r.id)
          ? { ...r, eSealHash, exported: true }
          : r
      ),
    }));
    
    // Add audit entry
    get().addAuditEntry(country || "EU", "export", `Exported reconciliation report with eSeal: ${eSealHash}`);
    
    // Download JSON
    const blob = new Blob([JSON.stringify({ ...report, eSealHash }, null, 2)], {
      type: "application/json",
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    const ts = new Date().toISOString().slice(0, 10);
    a.download = `sovereign-reconciliation-${country || "ALL"}-${ts}.json`;
    a.click();
    URL.revokeObjectURL(url);
    
    return eSealHash;
  },

  getCountryBalance: (country) => {
    const state = get();
    return state.countryBalances[country] || 0;
  },

  getReconciliationSummary: () => {
    const state = get();
    const byCountry: Record<EUCountry, { sent: number; received: number; net: number }> = {} as any;
    
    (Object.keys(state.countryBalances) as EUCountry[]).forEach((country) => {
      byCountry[country] = { sent: 0, received: 0, net: 0 };
    });
    
    state.reconciliations.forEach((r) => {
      if (!byCountry[r.fromCountry]) {
        byCountry[r.fromCountry] = { sent: 0, received: 0, net: 0 };
      }
      if (!byCountry[r.toCountry]) {
        byCountry[r.toCountry] = { sent: 0, received: 0, net: 0 };
      }
      
      byCountry[r.fromCountry].sent += r.amount;
      byCountry[r.toCountry].received += r.amount;
    });
    
    Object.keys(byCountry).forEach((country) => {
      const c = country as EUCountry;
      byCountry[c].net = byCountry[c].received - byCountry[c].sent;
    });
    
    return {
      totalReconciliations: state.reconciliations.length,
      totalAmount: state.reconciliations.reduce((sum, r) => sum + r.amount, 0),
      byCountry,
    };
  },

  addAuditEntry: (country, action, details, entryId) => {
    const entry: ComplianceAuditTrail = {
      id: generateId(),
      ts: Date.now(),
      country,
      action,
      details,
      entryId,
      verified: action === "verification",
    };
    
    set((state) => ({
      auditTrail: [...state.auditTrail, entry],
    }));
  },

  getAuditTrail: (country) => {
    const state = get();
    if (country) {
      return state.auditTrail.filter((a) => a.country === country);
    }
    return state.auditTrail;
  },
}));

